import { BrandSummaryDto, MeDto, WorkspaceSummaryDto } from "@growth/dto";
import { MemberRole } from "@growth/schema";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

import { useActiveStore } from "../stores/active";
import { useMe } from "./me";

const RANK: Record<MemberRole, number> = { VIEWER: 1, REVIEWER: 2, EDITOR: 3, OWNER: 4 };

/** The stored choice if it's still valid, otherwise the first workspace and its first brand. */
export const resolveActive = (
  me: MeDto | undefined,
  workspaceId: string | null,
  brandId: string | null,
): { workspace: WorkspaceSummaryDto | null; brand: BrandSummaryDto | null } => {
  const workspace =
    me?.workspaces.find((candidate) => candidate.id === workspaceId) ?? me?.workspaces[0] ?? null;
  const brand =
    workspace?.brands.find((candidate) => candidate.id === brandId) ?? workspace?.brands[0] ?? null;
  return { workspace, brand };
};

/**
 * Where the person is working right now, and what their role there allows. Keeps the stored
 * choice valid (a removed membership or archived brand falls back to the first one).
 */
export const useActive = () => {
  const me = useMe(true);
  const { workspaceId, brandId, set } = useActiveStore();
  const { workspace, brand } = resolveActive(me.data, workspaceId, brandId);

  useEffect(() => {
    if (!me.data) return;
    const nextWorkspace = workspace?.id ?? null;
    const nextBrand = brand?.id ?? null;
    if (nextWorkspace !== workspaceId || nextBrand !== brandId) set(nextWorkspace, nextBrand);
  }, [me.data, workspace?.id, brand?.id, workspaceId, brandId, set]);

  return useMemo(
    () => ({
      me: me.data,
      workspace,
      brand,
      role: workspace?.role ?? null,
      atLeast: (role: MemberRole) => (workspace ? RANK[workspace.role] >= RANK[role] : false),
    }),
    [me.data, workspace, brand],
  );
};

/**
 * Switches workspace or brand. Everything cached belongs to the old one, so it is dropped (and
 * in-flight requests ignored) before anything is shown for the new one.
 */
export const useSwitchActive = () => {
  const queryClient = useQueryClient();
  const set = useActiveStore((state) => state.set);
  return useCallback(
    async (workspaceId: string, brandId: string | null) => {
      const notMe = {
        predicate: (query: { queryKey: readonly unknown[] }) => query.queryKey[0] !== "me",
      };
      await queryClient.cancelQueries(notMe);
      set(workspaceId, brandId);
      queryClient.removeQueries(notMe);
    },
    [queryClient, set],
  );
};
